/* eslint-disable react/jsx-curly-newline */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { MenuOption } from '@saviynt/design-system';
import {
  AlertBanner,
  Badge,
  Box,
  Button,
  ButtonIcon,
  ButtonSelect,
  Chip,
  Icon,
  Illustration,
  ListRow,
  ListRowGroup,
  Menu,
  MenuItem,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import SodSearch from '../../../../shared/components/SodControls/SodSearch';
import {
  useInvertedLocalize,
  useInViewObserver,
  useTypedSelector,
} from '../../../../shared/hooks';
import type { ActiveAdvancedFilterState } from '../../../../shared/types/types';
import AuditAdvancedFilters from '../../AuditAdvancedFilters';
import { AuditLogDetailPanel } from '../../AuditLogDetailPanel';
import { InsightsPanel } from '../../AuditLogLeftSection';
import { AuditLogViewerBody } from '../../AuditLogViewerBody';
import AuditLogViewerRecordingFrame from '../../AuditLogViewerRecordingFrame';
import AuditLogViewerVideoPlaylist from '../../AuditLogViewerVideoPlaylist';
import { useSearchEntitlementsMutation } from '../../data-layer';
import type { EntitlementsSearchResponse } from '../../data-layer/auditLogTypes';
import {
  isFfRoleRequestType,
  resolveAuditLogEndpointDisplayName,
} from '../../data-layer/auditLogUtils';
import { auditLogActions } from '../../data-store';
import { useDateFormat } from '../../hooks';
import {
  preloadSessionRecordingAssets,
  useRecordingSelection,
  useRecordingSessionsViewModel,
} from '../../hooks/useRecordingSessions';
import {
  getRecordingPlaybackNotFoundLaunchIds,
  subscribeRecordingPlaybackNotFound,
} from '../../SessionRecording/playbackNotFound';
import SessionRecording, {
  prefetchRecordingPlayback,
} from '../../SessionRecording/SessionRecording';
import type { TimelineData } from '../../TimeLineBlock/types';
import isAbortError from '../../utils/isAbortError';
import { handleRecordingTimestampSelection } from '../../utils/recordingSelection';
import { AuditLogViewerAuditorTransactionSidePanel } from '../AuditLogViewerAuditorTransactionSidePanel';

import { CSS_CLASSES, TAB_CONFIG } from './constants';
import type {
  AuditLogViewerAuditorLeftBodyProps,
  TabType,
  TransactionItem,
} from './types';

import './AuditLogViewerAuditorLeftBody.css';

// Stable keys for skeleton placeholders to avoid using array index as key
const SKELETON_KEYS: string[] = [
  'tx-skel-1',
  'tx-skel-2',
  'tx-skel-3',
  'tx-skel-4',
  'tx-skel-5',
  'tx-skel-6',
  'tx-skel-7',
  'tx-skel-8',
  'tx-skel-9',
  'tx-skel-10',
];

// Reusable hook: auto-close a panel when a signature changes within a specific tab
function useAutoCloseOnSignatureChange(params: {
  requiredTab: 'all-logs' | 'transaction-overview';
  activeTab: string;
  isOpen: boolean;
  signature: string;
  onClose: () => void;
}): void {
  const { requiredTab, activeTab, isOpen, signature, onClose } = params;
  const prevSigRef = useRef<string | null>(null);

  useEffect(() => {
    if (activeTab !== requiredTab) {
      prevSigRef.current = signature;

      return;
    }

    if (!isOpen) {
      prevSigRef.current = signature;

      return;
    }

    if (prevSigRef.current && prevSigRef.current !== signature) {
      onClose();
    }

    prevSigRef.current = signature;
  }, [requiredTab, activeTab, isOpen, signature, onClose]);
}

/**
 * AuditLogViewerAuditorLeftBody Component
 * Left body section for the audit log viewer auditor view.
 * Implements tabs, search/filter controls, and transaction list.
 */
function AuditLogViewerAuditorLeftBody({
  className,
  activeTab: controlledActiveTab,
  onTabChange,
  transactionItems = [],
  onTransactionClick,
  onTransactionAction,
  onPanelStatesChange,
  isSmartFiltersOpen = true,
  onSmartFiltersClose,
  panelStates,
}: AuditLogViewerAuditorLeftBodyProps): React.ReactElement {
  const formatSecondsToHms = (val: unknown): string => {
    const total = typeof val === 'number' ? val : Number(val ?? 0);
    const seconds = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const parts: string[] = [];

    if (h > 0) {
      parts.push(`${h}h`);
    }

    if (m > 0) {
      parts.push(`${m}min`);
    }

    parts.push(`${s}s`);

    return parts.join(' ');
  };

  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const [internalActiveTab, setInternalActiveTab] = useState<TabType>(
    'transaction-overview'
  );
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilter] = useState('all');
  const [isTxFiltersOpen, setIsTxFiltersOpen] = useState(true);
  const [txItems, setTxItems] = useState<TransactionItem[]>([]);
  const [isTxLoading, setIsTxLoading] = useState(false);
  const [isTxError, setIsTxError] = useState(false);
  const [isTxLoaded, setIsTxLoaded] = useState(false);
  const [txReloadKey, setTxReloadKey] = useState(0);
  const [carryOverEntitlement, setCarryOverEntitlement] = useState<
    string | null
  >(null);
  const [openMenuItemId, setOpenMenuItemId] = useState<string | null>(null);
  const [afterKey, setAfterKey] = useState<number | string | null>(null);
  const [isTxObserverInView, setIsTxObserverInView] = useState(false);
  const txAbortRef = useRef<(() => void) | null>(null);
  const txListContainerRef = useRef<HTMLDivElement | null>(null);
  const TX_PAGE_SIZE = 10;

  const txListObserverElement = useInViewObserver(
    ({ isInView }) => setIsTxObserverInView(isInView),
    { root: txListContainerRef.current }
  );
  const activeTab = controlledActiveTab ?? internalActiveTab;

  const toBooleanArray = useCallback(
    (values: string[]): boolean[] =>
      values
        .map((v) => {
          const lv = String(v).toLowerCase();

          if (lv === 'yes' || lv === 'true') {
            return true;
          }

          if (lv === 'no' || lv === 'false') {
            return false;
          }

          return null;
        })
        .filter((x): x is boolean => x !== null),
    []
  );

  // Redux state hooks for filter pills
  const activeReduxFilters = useTypedSelector(
    (s) => s.aag.auditLog.activeEntitlementFilters
  );

  const isReadOnly = useTypedSelector((s) => s.aag.auditLog.isReadOnly);

  const requestMetadata = useTypedSelector(
    (s) => s.aag.auditLog.requestMetadata
  );
  const isVideoEnabled = !isFfRoleRequestType(requestMetadata?.requestType);
  const launchDetail = useTypedSelector((s) => s.aag.auditLog.launchDetail);
  const globalDateFormat = useDateFormat();
  const [excludedRecordingLaunchIds, setExcludedRecordingLaunchIds] = useState<
    Set<string>
  >(() => new Set());

  useEffect(() => {
    if (!isVideoEnabled) {
      return () => {};
    }

    const unsubscribe = subscribeRecordingPlaybackNotFound(
      (launchId: string) => {
        setExcludedRecordingLaunchIds((prev) => {
          if (prev.has(launchId)) {
            return prev;
          }

          const next = new Set(prev);

          next.add(launchId);

          return next;
        });
      }
    );

    return unsubscribe;
  }, [isVideoEnabled]);

  const recordingSessionsViewModel = useRecordingSessionsViewModel({
    launchDetail: isVideoEnabled ? launchDetail : [],
    requestMetadata,
    globalDateFormat,
    localize: l,
    excludedLaunchIds: excludedRecordingLaunchIds,
  });

  // Optimize: Start prefetching as soon as launchDetail is available (earlier than view model)
  useEffect(() => {
    if (!isVideoEnabled) {
      return;
    }

    if (Array.isArray(launchDetail) && launchDetail.length > 0) {
      // Preload assets immediately when we know recordings exist
      // Don't wait for this - let it load in background
      preloadSessionRecordingAssets().catch(() => {
        // Preload failures are non-blocking; assets load on demand
      });

      // Prefetch playback data for all sessions in parallel
      // Don't wait for this - let it load in background
      const prefetchPromises: Promise<void>[] = launchDetail
        .filter((session) => session.launchId && session.launchEndDate) // Only completed sessions
        .map((session) => prefetchRecordingPlayback(session.launchId));

      if (prefetchPromises.length > 0) {
        // Fire and forget - don't block UI on prefetch
        Promise.allSettled(prefetchPromises)
          .then(() => {
            setExcludedRecordingLaunchIds(
              new Set(getRecordingPlaybackNotFoundLaunchIds())
            );
            // Prefetch failures are non-blocking; playback loads on demand
          })
          .catch(() => {
            // Ignore errors - prefetch failures don't block UI
          });
      }
    }
  }, [launchDetail, isVideoEnabled]);

  const {
    recordedSessions,
    recordingSelectedIndex,
    setRecordingSelectedIndex,
    isRecordingPlayerView,
    setIsRecordingPlayerView,
    hasRecordedSessions,
  } = useRecordingSelection(recordingSessionsViewModel);
  const [mp4SeekSeconds, setMp4SeekSeconds] = useState<number | null>(null);

  const auditEndpoint = resolveAuditLogEndpointDisplayName(requestMetadata);
  const openTransactionId = useTypedSelector(
    (s) => s.aag.auditLog.openTransactionId
  );

  const filtersSignature = useMemo(() => {
    try {
      const compact = activeReduxFilters
        .map((f: ActiveAdvancedFilterState) => ({
          value: f.value,
          method: f.method,
          options: f.options.map((o: MenuOption) => String(o.value)).sort(),
        }))
        .sort((a, b) => a.value.localeCompare(b.value));

      return JSON.stringify(compact);
    } catch {
      return '';
    }
  }, [activeReduxFilters]);

  const [searchEntitlementsTrigger] = useSearchEntitlementsMutation();

  useEffect(() => {
    if (
      activeTab === 'transaction-overview' &&
      requestMetadata?.requestAccessKey != null
    ) {
      // Build filters snapshot once per effect run to avoid dependency loops
      const pick = (key: string) =>
        (activeReduxFilters.find((f) => f.value === key)?.options ?? []).map(
          (o) => String(o.value)
        );

      const payload = {
        requestAccessKey: requestMetadata.requestAccessKey,

        filters: {
          logTypes: pick('logTypes'),
          entitlementValues: pick('entitlementDisplayNames'),
          severities: pick('severities'),
          entitlementDeviations: toBooleanArray(pick('entitlementDeviation')),
          functionNames: pick('functionNames'),
          searchText: searchValue.length >= 2 ? searchValue : '',
        },
        afterKey: null,
        pageSize: TX_PAGE_SIZE,
      };

      setAfterKey(null);
      setIsTxLoading(true);
      setIsTxError(false);
      // keep isTxLoaded as-is to avoid flicker during pagination
      setTxItems([]);
      // Reset open row on data change as requested
      dispatch(auditLogActions.setOpenTransactionId(null));

      // cancel any in-flight request
      txAbortRef.current?.();
      const controller = searchEntitlementsTrigger(payload);

      txAbortRef.current = () => controller.abort();

      controller
        .unwrap()
        .then((res: EntitlementsSearchResponse) => {
          const mapped: TransactionItem[] = res.data.map((r) => ({
            id: String(r.entitlementValueKey),
            name: r.entitlementDescription ?? r.entitlementValue,
            tCode: r.entitlementValue,
            usageCount: Number(r.entitlementUsageCount),
            usageDuration: formatSecondsToHms(r.entitlementUsageDuration),
            hasDeviation: Boolean(r.entitlementDeviation),
            functionNames: Array.isArray(r.functionNames)
              ? r.functionNames.map((fn: unknown) => String(fn))
              : undefined,
            businessProcessNames: Array.isArray(r.businessProcessNames)
              ? r.businessProcessNames.map((bp: unknown) => String(bp))
              : undefined,
          }));

          setTxItems(mapped);
          setAfterKey(res.afterKey ?? null);
        })
        .catch((err: unknown) => {
          // Aborts are expected when switching tabs / changing filters quickly.
          if (isAbortError(err)) {
            return;
          }

          setIsTxError(true);
        })
        .finally(() => {
          setIsTxLoading(false);
          // keep loaded true after first page to avoid blank flicker on load-more
          setIsTxLoaded(true);
        });
    }
  }, [
    activeTab,
    filtersSignature,
    activeReduxFilters,
    searchEntitlementsTrigger,
    dispatch,
    toBooleanArray,
    searchValue,
    txReloadKey,
    requestMetadata,
  ]);

  // Load next page on demand when user requests more, this will be scroll observer based
  const handleLoadMore = useCallback(() => {
    if (
      activeTab === 'transaction-overview' &&
      !isTxLoading &&
      requestMetadata?.requestAccessKey != null
    ) {
      const pick = (key: string) =>
        (activeReduxFilters.find((f) => f.value === key)?.options ?? []).map(
          (o) => String(o.value)
        );

      const payload = {
        requestAccessKey: requestMetadata.requestAccessKey,
        filters: {
          logTypes: pick('logTypes'),
          entitlementValues: pick('entitlementDisplayNames'),
          severities: pick('severities'),
          entitlementDeviations: toBooleanArray(pick('entitlementDeviation')),
          functionNames: pick('functionNames'),
          searchText: searchValue.length >= 3 ? searchValue : '',
        },
        afterKey,
        pageSize: TX_PAGE_SIZE,
      };

      setIsTxLoading(true);
      setIsTxError(false);
      setIsTxLoaded(false);

      // cancel any in-flight request
      txAbortRef.current?.();
      const controller = searchEntitlementsTrigger(payload);

      txAbortRef.current = () => controller.abort();

      controller
        .unwrap()
        .then((res: EntitlementsSearchResponse) => {
          const mapped: TransactionItem[] = res.data.map((r) => ({
            id: String(r.entitlementValueKey),
            name: r.entitlementDescription ?? r.entitlementValue,
            tCode: r.entitlementValue,
            usageCount: Number(r.entitlementUsageCount || 0),
            usageDuration: formatSecondsToHms(r.entitlementUsageDuration),
            hasDeviation: Boolean(r.entitlementDeviation),
            functionNames: Array.isArray(r.functionNames)
              ? r.functionNames.map((fn: unknown) => String(fn))
              : undefined,
            businessProcessNames: Array.isArray(r.businessProcessNames)
              ? r.businessProcessNames.map((bp: unknown) => String(bp))
              : undefined,
          }));

          setTxItems((prev) => [...prev, ...mapped]);
          setAfterKey(res.afterKey ?? null);
        })
        .catch((err: unknown) => {
          // Aborts are expected when switching tabs / changing filters quickly.
          if (isAbortError(err)) {
            return;
          }

          setIsTxError(true);
        })
        .finally(() => {
          setIsTxLoading(false);
          setIsTxLoaded(true);
        });
    }
  }, [
    activeTab,
    isTxLoading,
    activeReduxFilters,
    afterKey,
    searchValue,
    searchEntitlementsTrigger,
    toBooleanArray,
    requestMetadata,
  ]);

  // Trigger next page when sentinel becomes visible inside the scroll container
  useEffect(() => {
    if (
      activeTab === 'transaction-overview' &&
      isTxObserverInView &&
      !isTxLoading &&
      isTxLoaded &&
      afterKey !== null
    ) {
      handleLoadMore();
    }
  }, [
    activeTab,
    isTxObserverInView,
    isTxLoading,
    isTxLoaded,
    afterKey,
    handleLoadMore,
  ]);

  // Auto-fetch while container is not scrollable (content doesn't fill the viewport)
  useEffect(() => {
    if (activeTab !== 'transaction-overview') {
      return;
    }

    const container = txListContainerRef.current;

    if (!container) {
      return;
    }

    const needsMore = container.scrollHeight <= container.clientHeight;

    if (needsMore && !isTxLoading && isTxLoaded && afterKey !== null) {
      // Defer to next tick to avoid setState during render
      setTimeout(() => {
        handleLoadMore();
      }, 0);
    }
  }, [
    activeTab,
    txItems.length,
    isTxLoading,
    isTxLoaded,
    afterKey,
    handleLoadMore,
  ]);

  // Side panel state for All Logs tab
  const [selectedTimelineItem, setSelectedTimelineItem] =
    useState<TimelineData | null>(null);
  const [openTimelineItemId, setOpenTimelineItemId] = useState<string | null>(
    null
  );
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [detailPanelActiveTab, setDetailPanelActiveTab] = useState<
    'summary' | 'review-activity'
  >('summary');

  // Insights panel state for All Logs tab
  // Note: In readOnly mode (auditor view), insights panel should not be shown
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);

  // Recording panel state for All Logs tab
  const [isRecordingOpen, setIsRecordingOpen] = useState(false);
  const [noRecordingBanner, setNoRecordingBanner] = useState<{
    key: number;
    message: string;
  } | null>(null);
  const closeRecordingPanel = useCallback(() => {
    if (!isRecordingOpen) {
      return;
    }

    setIsRecordingOpen(false);
    onPanelStatesChange?.({ isRecordingOpen: false });
    setIsRecordingPlayerView(false);
    setMp4SeekSeconds(null);
    setOpenTimelineItemId(null);
  }, [
    isRecordingOpen,
    onPanelStatesChange,
    setIsRecordingOpen,
    setIsRecordingPlayerView,
    setMp4SeekSeconds,
  ]);

  const openRecordingPanel = useCallback(() => {
    if (!isVideoEnabled) {
      return;
    }

    setIsRecordingOpen(true);

    // Only timestamp-driven navigation should seek within MP4 playback.
    // Manual open should reset the seek target.
    setMp4SeekSeconds(null);

    onPanelStatesChange?.({ isRecordingOpen: true });

    // Close smart filters if recording is being opened
    if (isSmartFiltersOpen && onSmartFiltersClose) {
      onSmartFiltersClose();
    }

    // Close insights panel if recording is being opened
    if (isInsightsOpen) {
      setIsInsightsOpen(false);
      onPanelStatesChange?.({ isInsightsOpen: false });
    }

    // Close detail panel when opening the recording panel
    if (isDetailPanelOpen) {
      const logIdToKeep = selectedTimelineItem?.uniqueLogId;

      if (logIdToKeep != null) {
        setOpenTimelineItemId(logIdToKeep);
      }

      setIsDetailPanelOpen(false);
      setSelectedTimelineItem(null);
      onPanelStatesChange?.({ isDetailPanelOpen: false });
    }
  }, [
    isSmartFiltersOpen,
    onSmartFiltersClose,
    isInsightsOpen,
    isDetailPanelOpen,
    selectedTimelineItem,
    onPanelStatesChange,
    setIsRecordingOpen,
    setIsInsightsOpen,
    setIsDetailPanelOpen,
    setOpenTimelineItemId,
    setSelectedTimelineItem,
    setMp4SeekSeconds,
    isVideoEnabled,
  ]);

  // Transaction side panel state for Transaction Overview tab
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionItem | null>(null);

  const activeFiltersSig = useTypedSelector((s) =>
    JSON.stringify(s.aag.auditLog.activeFilters)
  );
  const searchQueryRaw = useTypedSelector((s) =>
    String(s.aag.auditLog.searchQuery)
  );

  const closeSig = useMemo(
    () => `${activeFiltersSig}|${searchQueryRaw}`,
    [activeFiltersSig, searchQueryRaw]
  );

  useAutoCloseOnSignatureChange({
    requiredTab: 'all-logs',
    activeTab,
    isOpen: isDetailPanelOpen,
    signature: closeSig,
    onClose: () => {
      setIsDetailPanelOpen(false);
      setSelectedTimelineItem(null);
      setOpenTimelineItemId(null);
      onPanelStatesChange?.({ isDetailPanelOpen: false });
    },
  });

  // Close All Logs detail panel when Transaction Overview filters/search change
  const txCloseSig = useMemo(
    () => `${filtersSignature}|${searchValue}`,
    [filtersSignature, searchValue]
  );

  useAutoCloseOnSignatureChange({
    requiredTab: 'transaction-overview',
    activeTab,
    isOpen: Boolean(panelStates.isTransactionSidePanelOpen),
    signature: txCloseSig,
    onClose: () => {
      onPanelStatesChange?.({ isTransactionSidePanelOpen: false });
      setSelectedTransaction(null);
    },
  });

  // Sync local detail panel state with parent panel states
  useEffect(() => {
    if (!panelStates.isDetailPanelOpen && isDetailPanelOpen) {
      setIsDetailPanelOpen(false);
      setSelectedTimelineItem(null);
      setOpenTimelineItemId(null);
    }
  }, [panelStates.isDetailPanelOpen, isDetailPanelOpen]);

  // Sync local recording panel state with parent panel states
  useEffect(() => {
    if (!panelStates.isRecordingOpen && isRecordingOpen) {
      setIsRecordingOpen(false);
    }
  }, [panelStates.isRecordingOpen, isRecordingOpen]);

  useEffect(() => {
    if (!isVideoEnabled && isRecordingOpen) {
      closeRecordingPanel();
    }
  }, [isVideoEnabled, isRecordingOpen, closeRecordingPanel]);

  // Ensure filters are open when switching to Transaction Overview tab
  // Use a ref to track previous tab to only open filters on tab switch, not on filter state changes
  const prevActiveTabRef = useRef(activeTab);

  useEffect(() => {
    const prevTab = prevActiveTabRef.current;

    prevActiveTabRef.current = activeTab;

    // Only open filters when switching TO transaction-overview tab, not when already on it
    if (
      activeTab === 'transaction-overview' &&
      prevTab !== 'transaction-overview' &&
      !isTxFiltersOpen
    ) {
      setIsTxFiltersOpen(true);
    }
  }, [activeTab, isTxFiltersOpen]);

  // Close smart filters when transaction side panel opens
  useEffect(() => {
    if (
      panelStates.isTransactionSidePanelOpen &&
      isSmartFiltersOpen &&
      onSmartFiltersClose
    ) {
      onSmartFiltersClose();
    }
  }, [
    panelStates.isTransactionSidePanelOpen,
    isSmartFiltersOpen,
    onSmartFiltersClose,
  ]);

  const handleTabChange = useCallback(
    (value: string) => {
      const newTab = value as TabType;

      // Clear audit log data when switching away from all-logs
      if (activeTab === 'all-logs' && newTab !== 'all-logs') {
        dispatch(auditLogActions.clearData());
      }

      // Clear transaction data when switching away from transaction-overview
      if (
        activeTab === 'transaction-overview' &&
        newTab !== 'transaction-overview'
      ) {
        setTxItems([]);
        setIsTxLoaded(false);
      }

      // Close any open side panels when switching tabs
      if (panelStates.isTransactionSidePanelOpen) {
        onPanelStatesChange?.({ isTransactionSidePanelOpen: false });
        setSelectedTransaction(null);
      }

      if (isDetailPanelOpen) {
        setIsDetailPanelOpen(false);
        setSelectedTimelineItem(null);
        setOpenTimelineItemId(null);
        onPanelStatesChange?.({ isDetailPanelOpen: false });
      }

      if (onTabChange) {
        onTabChange(newTab);
      } else {
        setInternalActiveTab(newTab);
      }

      // Persist active tab to Redux for filters context
      dispatch(
        auditLogActions.setActiveTab(
          newTab as 'all-logs' | 'transaction-overview'
        )
      );

      // If switching to All Logs, clear Redux search so payload doesn't carry previous text
      if (newTab === 'all-logs') {
        dispatch(auditLogActions.setSearchQuery(''));
        dispatch(auditLogActions.resetSearchAfter());
      }

      // Clear Transaction Overview search on any tab switch
      setSearchValue('');

      // Close transaction-overview filters UI when navigating away
      if (newTab === 'all-logs') {
        setIsTxFiltersOpen(false);
      }
    },
    [
      activeTab,
      panelStates.isTransactionSidePanelOpen,
      isDetailPanelOpen,
      onTabChange,
      onPanelStatesChange,
      dispatch,
    ]
  );

  const handleTransactionClick = useCallback(
    (item: TransactionItem) => {
      if (onTransactionClick) {
        onTransactionClick(item);
      }

      // Close smart filters if they're open
      if (isSmartFiltersOpen && onSmartFiltersClose) {
        onSmartFiltersClose();
      }

      // Open transaction side panel for Transaction Overview tab
      if (activeTab === 'transaction-overview') {
        setSelectedTransaction(item);
        onPanelStatesChange?.({ isTransactionSidePanelOpen: true });
        // Persist open row id in Redux, do not toggle close on same row
        dispatch(auditLogActions.setOpenTransactionId(item.id));
      }
    },
    [
      onTransactionClick,
      activeTab,
      isSmartFiltersOpen,
      onSmartFiltersClose,
      onPanelStatesChange,
      dispatch,
    ]
  );

  const handleTransactionAction = useCallback(
    (item: TransactionItem, action: string) => {
      if (onTransactionAction) {
        onTransactionAction(item, action);
      }

      // If user clicks view-logs, carry the entitlement value to All Logs filters
      if (action === 'view-logs') {
        setCarryOverEntitlement(item.tCode);
      }
    },
    [onTransactionAction]
  );

  const handleTimelineItemClickRef = useRef<
    (item: TimelineData, tab?: 'summary' | 'review-activity') => void
  >(() => {});

  handleTimelineItemClickRef.current = (
    item: TimelineData,
    tab?: 'summary' | 'review-activity'
  ) => {
    const openDetailPanel = () => {
      setSelectedTimelineItem(item);
      setIsDetailPanelOpen(true);
      setDetailPanelActiveTab(tab ?? 'summary');

      onPanelStatesChange?.({ isDetailPanelOpen: true });

      // Close smart filters if detail panel is being opened
      if (isSmartFiltersOpen && onSmartFiltersClose) {
        onSmartFiltersClose();
      }
    };

    if (isRecordingOpen) {
      closeRecordingPanel();
    }

    openDetailPanel();

    // Auditors are read-only; skip fetching reviews and local latestActivity updates here
  };

  const handleTimelineItemClick = useCallback(
    (item: TimelineData, tab?: 'summary' | 'review-activity') => {
      handleTimelineItemClickRef.current(item, tab);
    },
    []
  );

  const handleDetailPanelClose = useCallback(() => {
    setIsDetailPanelOpen(false);
    setSelectedTimelineItem(null);
    setOpenTimelineItemId(null);

    onPanelStatesChange?.({ isDetailPanelOpen: false });
  }, [onPanelStatesChange]);

  const handleInsightsToggle = useCallback(() => {
    const newInsightsOpen = !isInsightsOpen;

    setIsInsightsOpen(newInsightsOpen);

    onPanelStatesChange?.({ isInsightsOpen: newInsightsOpen });

    // Close detail panel if insights is being opened
    if (newInsightsOpen && isDetailPanelOpen) {
      setIsDetailPanelOpen(false);
      setSelectedTimelineItem(null);
      setOpenTimelineItemId(null);

      onPanelStatesChange?.({ isDetailPanelOpen: false });
    }
  }, [isInsightsOpen, isDetailPanelOpen, onPanelStatesChange]);

  const handleInsightsClose = useCallback(() => {
    setIsInsightsOpen(false);

    onPanelStatesChange?.({ isInsightsOpen: false });
  }, [onPanelStatesChange]);

  // Resolve selected item from Redux so detail panel always shows latest rowLogStatus (e.g. COMMENTED after adding a comment)
  const reduxTimelineData = useTypedSelector(
    (s) => s.aag.auditLog.timelineData
  ) as TimelineData[] | undefined;
  const resolvedDetailPanelItem = useMemo(() => {
    if (!selectedTimelineItem) {
      return null;
    }

    const fromRedux = Array.isArray(reduxTimelineData)
      ? reduxTimelineData.find(
          (t) =>
            String(t.uniqueLogId) === String(selectedTimelineItem.uniqueLogId)
        )
      : undefined;

    return fromRedux ?? selectedTimelineItem;
  }, [reduxTimelineData, selectedTimelineItem]);

  // Close detail panel once per /search loading cycle in All Logs (uses Redux isLoading)
  const isAllLogsLoading = useTypedSelector((s) => s.aag.auditLog.isLoading);
  const prevAllLogsLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    const wasLoading = prevAllLogsLoadingRef.current;

    if (
      activeTab === 'all-logs' &&
      !wasLoading &&
      isAllLogsLoading &&
      isDetailPanelOpen
    ) {
      setIsDetailPanelOpen(false);
      setSelectedTimelineItem(null);
      setOpenTimelineItemId(null);
      onPanelStatesChange?.({ isDetailPanelOpen: false });
    }

    prevAllLogsLoadingRef.current = isAllLogsLoading;
  }, [activeTab, isAllLogsLoading, isDetailPanelOpen, onPanelStatesChange]);

  const handleRecordingToggle = useCallback(() => {
    if (isRecordingOpen) {
      closeRecordingPanel();
    } else {
      openRecordingPanel();
    }
  }, [isRecordingOpen, closeRecordingPanel, openRecordingPanel]);

  const openRecordingPanelForTimestampSelection = useCallback(
    (_selectedIdx: number) => {
      if (!isVideoEnabled) {
        return;
      }

      // _selectedIdx is provided by the shared util; we don't need it here because
      // auditor view prefetches all session playback early.
      if (_selectedIdx < 0) {
        return;
      }

      const nextPanelState: {
        isRecordingOpen?: boolean;
        isInsightsOpen?: boolean;
        isDetailPanelOpen?: boolean;
      } = {};

      if (!isRecordingOpen) {
        setIsRecordingOpen(true);
        nextPanelState.isRecordingOpen = true;
      }

      if (isInsightsOpen) {
        setIsInsightsOpen(false);
        nextPanelState.isInsightsOpen = false;
      }

      if (isDetailPanelOpen) {
        // Preserve which timeline row to keep expanded so connector to Session Recording still shows
        const logIdToKeep = selectedTimelineItem?.uniqueLogId;

        if (logIdToKeep != null) {
          setOpenTimelineItemId(logIdToKeep);
        }

        setIsDetailPanelOpen(false);
        setSelectedTimelineItem(null);
        nextPanelState.isDetailPanelOpen = false;
      }

      if (
        nextPanelState.isRecordingOpen ||
        nextPanelState.isInsightsOpen ||
        nextPanelState.isDetailPanelOpen
      ) {
        onPanelStatesChange?.(nextPanelState);
      }
    },
    [
      isRecordingOpen,
      isInsightsOpen,
      isDetailPanelOpen,
      selectedTimelineItem,
      onPanelStatesChange,
      setIsRecordingOpen,
      setIsInsightsOpen,
      setIsDetailPanelOpen,
      setOpenTimelineItemId,
      setSelectedTimelineItem,
      isVideoEnabled,
    ]
  );

  const handleTimelineTimestampClick = useCallback(
    (item: TimelineData) => {
      if (!isVideoEnabled) {
        return;
      }

      const nextSeek = handleRecordingTimestampSelection({
        timestampIso: item.logCreatedAt,
        sessions: recordedSessions,
        localize: l,
        setNoRecordingBanner,
        setRecordingSelectedIndex,
        setIsRecordingPlayerView,
        openRecordingPanel: openRecordingPanelForTimestampSelection,
      });

      setMp4SeekSeconds(nextSeek);

      if (nextSeek !== null && isSmartFiltersOpen && onSmartFiltersClose) {
        onSmartFiltersClose();
      }

      // Keep row connector behavior aligned with non-readOnly path:
      // - No recording + detail panel closed: collapse expanded row
      // - Has recording + detail panel closed: expand clicked row
      if (nextSeek === null && !isDetailPanelOpen) {
        setOpenTimelineItemId(null);
      } else if (nextSeek !== null && !isDetailPanelOpen) {
        setOpenTimelineItemId(item.uniqueLogId);
      }
    },
    [
      l,
      recordedSessions,
      isDetailPanelOpen,
      isSmartFiltersOpen,
      onSmartFiltersClose,
      setNoRecordingBanner,
      setRecordingSelectedIndex,
      setIsRecordingPlayerView,
      openRecordingPanelForTimestampSelection,
      setMp4SeekSeconds,
      isVideoEnabled,
    ]
  );

  const handleDetailPanelUtcTimestampClick = useCallback(() => {
    if (!isVideoEnabled) {
      return;
    }

    const nextSeek = handleRecordingTimestampSelection({
      timestampIso: selectedTimelineItem?.logCreatedAt,
      sessions: recordedSessions,
      localize: l,
      setNoRecordingBanner,
      setRecordingSelectedIndex,
      setIsRecordingPlayerView,
      openRecordingPanel: openRecordingPanelForTimestampSelection,
    });

    setMp4SeekSeconds(nextSeek);
  }, [
    l,
    selectedTimelineItem,
    recordedSessions,
    setNoRecordingBanner,
    setRecordingSelectedIndex,
    setIsRecordingPlayerView,
    openRecordingPanelForTimestampSelection,
    setMp4SeekSeconds,
    isVideoEnabled,
  ]);

  const handleTransactionSidePanelClose = useCallback(() => {
    onPanelStatesChange?.({ isTransactionSidePanelOpen: false });
    setSelectedTransaction(null);
    dispatch(auditLogActions.setOpenTransactionId(null));
  }, [onPanelStatesChange, dispatch]);

  const handleViewLogs = useCallback(() => {
    // Carry over entitlementValue (tCode) from selected transaction if available
    if (selectedTransaction?.tCode) {
      setCarryOverEntitlement(selectedTransaction.tCode);
    }

    // Close transaction side panel and switch to All Logs tab
    onPanelStatesChange?.({ isTransactionSidePanelOpen: false });
    setSelectedTransaction(null);
    setIsTxFiltersOpen(false);

    // Switch to All Logs tab
    const newTab: TabType = 'all-logs';

    if (onTabChange) {
      onTabChange(newTab);
    } else {
      setInternalActiveTab(newTab);
    }

    // Persist active tab to Redux so downstream components load All Logs filters
    dispatch(auditLogActions.setActiveTab('all-logs'));
  }, [onTabChange, onPanelStatesChange, dispatch, selectedTransaction]);

  // Search handler for SodSearch component
  const handleSearch = useCallback((searchText: string) => {
    setSearchValue(searchText);
  }, []);

  // Get base items (prioritize txItems over transactionItems)
  const baseItems = useMemo(
    () => (txItems.length > 0 ? txItems : transactionItems),
    [txItems, transactionItems]
  );

  // Filter and sort transactions
  const filteredAndSortedItems = useMemo(() => {
    let filtered = baseItems;

    // Apply search filter
    if (searchValue) {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.tCode.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply category filter
    switch (selectedFilter) {
      case 'deviation':
        filtered = filtered.filter((item) => item.hasDeviation);
        break;
      case 'no-deviation':
        filtered = filtered.filter((item) => !item.hasDeviation);
        break;
      case 'high-usage':
        filtered = filtered.filter((item) => item.usageCount >= 10);
        break;
      case 'low-usage':
        filtered = filtered.filter((item) => item.usageCount < 10);
        break;
      default:
        // 'all' - no additional filtering
        break;
    }

    // Do not sort; preserve API order to prevent reordering/flicker on append
    return filtered;
  }, [baseItems, searchValue, selectedFilter]);

  const renderTransactionItem = useCallback(
    (item: TransactionItem) => {
      // Center Content (Icon, Title, Chip, and Details)
      const centerContent = (
        <Box className='AuditLogViewerAuditorLeftBody-transactionCenterContent'>
          {/* Leading Icon */}
          <Box className={CSS_CLASSES.TRANSACTION_ICON}>
            <Icon
              kind='Entitlement'
              size='medium'
              color='--color-foreground-neutral-medium'
            />
          </Box>

          {/* Content */}
          <Box className={CSS_CLASSES.TRANSACTION_CONTENT}>
            {/* Header with title and deviation chip */}
            <Box className={CSS_CLASSES.TRANSACTION_HEADER}>
              <Typography
                lineLimit={3}
                kind='h3'
                color='--color-foreground-neutral-bold'
                className={CSS_CLASSES.TRANSACTION_TITLE}>
                {item.name}
              </Typography>
              {item.hasDeviation && (
                <Chip
                  kind='outlined'
                  size='xxSmall'
                  label={l('Deviate')}
                  color='critical'
                  leftIcon={
                    <Icon
                      kind='AlertCriticalOutline'
                      color='critical'
                      size='xSmall'
                    />
                  }
                />
              )}
            </Box>

            {/* Transaction Details */}
            <Box className={CSS_CLASSES.TRANSACTION_DETAILS}>
              <Box className={CSS_CLASSES.TRANSACTION_ATTRIBUTE}>
                <Typography
                  lineLimit={3}
                  kind='body3'
                  color='--color-foreground-neutral-subtle'>
                  {l('Entitlement: ')}
                </Typography>
                <Typography
                  lineLimit={3}
                  kind='body3'
                  color='--color-foreground-neutral-bold'>
                  {item.tCode}
                </Typography>
              </Box>

              <Typography
                lineLimit={3}
                kind='body3'
                color='--color-foreground-neutral-bold'>
                {l('•')}
              </Typography>

              <Box className={CSS_CLASSES.TRANSACTION_ATTRIBUTE}>
                <Typography
                  lineLimit={3}
                  kind='body3'
                  color='--color-foreground-neutral-subtle'>
                  {l('Usage Count:')}
                </Typography>
                <Typography
                  lineLimit={3}
                  kind='body3'
                  color='--color-foreground-neutral-bold'>
                  {item.usageCount}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      );

      // Trailing Content (Action Buttons)
      const getKebabMenu = () => (
        <Box className='AuditLogViewerAuditorLeftBody-kebabMenu'>
          <MenuItem
            option={{ label: l('View Logs'), value: 'view-logs' }}
            // suffixIcon={<Icon kind='ArrowRight' size='small' />}
            onClick={() => {
              handleTransactionAction(item, 'view-logs');

              if (onTabChange) {
                onTabChange('all-logs');
              } else {
                setInternalActiveTab('all-logs');
              }

              // Persist active tab to Redux and close TX filters panel to avoid leaking pills
              dispatch(auditLogActions.setActiveTab('all-logs'));
              setIsTxFiltersOpen(false);

              if (panelStates.isTransactionSidePanelOpen) {
                onPanelStatesChange?.({ isTransactionSidePanelOpen: false });
                setSelectedTransaction(null);
              }

              if (isDetailPanelOpen) {
                setIsDetailPanelOpen(false);
                setSelectedTimelineItem(null);
                setOpenTimelineItemId(null);
                onPanelStatesChange?.({ isDetailPanelOpen: false });
              }

              setOpenMenuItemId(null);
            }}
          />
        </Box>
      );

      const trailingContent = (
        <Box className={CSS_CLASSES.TRANSACTION_ACTIONS}>
          <ButtonIcon
            size='medium'
            kind='outlinedSecondary'
            icon={<Icon kind='ArrowRight' size='medium' />}
            ariaLabel={l('View Logs')}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setSelectedTransaction(item);
              dispatch(auditLogActions.setOpenTransactionId(item.id));
              onPanelStatesChange?.({ isTransactionSidePanelOpen: true });
            }}
          />
          {/* kebob menu with view logs option */}
          <Menu
            CustomMenu={getKebabMenu()}
            isPortalEnabled
            isMenuExpanded={openMenuItemId === item.id}
            setIsExpanded={(expanded: boolean) => {
              setOpenMenuItemId(expanded ? item.id : null);
            }}
            trigger={
              <ButtonIcon
                kind='ghost'
                size='medium'
                ariaLabel={l('More')}
                icon={<Icon kind='MoreVertical' />}
              />
            }
            className='AuditLogViewerAuditorLeftBody-kebabMenu-container'
          />
        </Box>
      );

      return (
        <ListRow
          key={item.id}
          className={`${
            CSS_CLASSES.TRANSACTION_ITEM
          } AuditLogViewerAuditorLeftBody-transactionClickable${
            openTransactionId === item.id
              ? ' AuditLogViewerAuditorLeftBody-transactionItem--open'
              : ''
          }`}
          centerContent={centerContent}
          trailingContent={trailingContent}
          isListItem={false}
          isCheckboxDisabled
          handleRowClick={() => handleTransactionClick(item)}
          isRowDetailsOpen={openTransactionId === item.id}
          connectorDistance='34px'
        />
      );
    },
    [
      l,
      handleTransactionClick,
      handleTransactionAction,
      onTabChange,
      panelStates.isTransactionSidePanelOpen,
      isDetailPanelOpen,
      onPanelStatesChange,
      setInternalActiveTab,
      setSelectedTransaction,
      setSelectedTimelineItem,
      openMenuItemId,
      dispatch,
      setIsTxFiltersOpen,
      openTransactionId,
    ]
  );

  return (
    <Box
      className={cx(CSS_CLASSES.ROOT, className, {
        'AuditLogViewerAuditorLeftBody--smartFiltersClosed':
          !isSmartFiltersOpen,
        'AuditLogViewerAuditorLeftBody--readOnly': isReadOnly,
        'AuditLogViewerAuditorLeftBody--withTransactionSidePanel': Boolean(
          panelStates.isTransactionSidePanelOpen
        ),
        'AuditLogViewerAuditorLeftBody--withRecordingPanel': isRecordingOpen,
      })}>
      <AlertBanner
        key={noRecordingBanner?.key ?? 'no-recording'}
        colorTheme='Information'
        title={l('No Session Recording Found')}
        description={noRecordingBanner?.message ?? ''}
        isVisible={Boolean(noRecordingBanner)}
        onCancel={() => setNoRecordingBanner(null)}
        isFloating
        shouldAutoDismiss
        autoDismissDelay={3000}
      />
      {/* Tabs Section */}
      <Box className={CSS_CLASSES.TABS_SECTION}>
        <Box className={CSS_CLASSES.TABS}>
          <TabGroup value={activeTab} onChange={handleTabChange}>
            <TabItem
              label={l(TAB_CONFIG['transaction-overview'].label)}
              value={TAB_CONFIG['transaction-overview'].value}
            />
            <TabItem
              label={l(TAB_CONFIG['all-logs'].label)}
              value={TAB_CONFIG['all-logs'].value}
            />
          </TabGroup>
        </Box>
      </Box>

      {/* Table Section */}
      <Box
        className={cx(CSS_CLASSES.TABLE_SECTION, {
          [CSS_CLASSES.ALL_LOGS]: activeTab === 'all-logs',
          'AuditLogViewerAuditorLeftBody-allLogs--detailOpen':
            activeTab === 'all-logs' && isReadOnly && isDetailPanelOpen,
        })}>
        {activeTab === 'all-logs' ? (
          /* All Logs Tab - Show AuditLogViewerBody */
          <AuditLogViewerBody
            isInsightsOpen={isInsightsOpen}
            onInsightsToggle={handleInsightsToggle}
            onTimelineItemClick={handleTimelineItemClick}
            onTimelineTimestampClick={
              isVideoEnabled ? handleTimelineTimestampClick : undefined
            }
            selectedTimelineItemId={
              openTimelineItemId ??
              selectedTimelineItem?.uniqueLogId ??
              undefined
            }
            isDetailPanelOpen={isDetailPanelOpen}
            isRecordingOpen={isRecordingOpen}
            onRecordingToggle={handleRecordingToggle}
            showInsightsButton={false}
            initialActiveFilters={
              carryOverEntitlement
                ? [
                    {
                      value: 'entitlementDisplayNames',
                      method: 'SEARCH',
                      options: [
                        {
                          label: carryOverEntitlement,
                          value: carryOverEntitlement,
                        },
                      ],
                    },
                  ]
                : undefined
            }
            onClearCarryOverEntitlement={() => setCarryOverEntitlement(null)}
          />
        ) : (
          /* Transaction Overview Tab - Show current content */
          <>
            {/* List Controls */}
            <Box className={CSS_CLASSES.LIST_CONTROLS}>
              {/* Filter Menu */}
              <ButtonSelect
                kind='rectangle'
                label={l('Filter')}
                onClick={() => setIsTxFiltersOpen((prev) => !prev)}
                isOpen={isTxFiltersOpen}
                size='medium'
                prefixIcon={<Icon kind='Filter' />}
                BadgeComp={
                  activeReduxFilters.length > 0 ? (
                    <Badge
                      kind='number'
                      color='primary'
                      size='small'
                      number={activeReduxFilters.length}
                    />
                  ) : null
                }
                isVisualCueEnabled={false}
              />
              {/* Search Input */}
              <SodSearch
                searchPlaceholder={l('Search')}
                inlineMesssage={l(
                  'Type more than 2 characters to see results.'
                )}
                searchDebounce={300}
                isDisabled={false}
                handleSearch={handleSearch}
                className={CSS_CLASSES.SEARCH_INPUT}
                inputFieldClassName='AuditLogViewerAuditorLeftBody-searchInput'
                defaultValue={searchValue}
              />
            </Box>

            {/* Transaction Filters (pills) */}
            <AuditAdvancedFilters
              activeFilters={activeReduxFilters}
              activeContextOverride={activeTab}
              handleFilterChange={() => {}}
              handleOptionsClick={(key, method, selectedOptions) => {
                const existing = activeReduxFilters.filter(
                  (f) => f.value !== key
                );
                const next: ActiveAdvancedFilterState[] =
                  selectedOptions.length === 0
                    ? existing
                    : [
                        ...existing,
                        { value: key, method, options: selectedOptions },
                      ];

                dispatch(auditLogActions.setActiveEntitlementFilters(next));
              }}
              handleFilterSearch={(): Promise<MenuOption[]> =>
                Promise.resolve([] as MenuOption[])
              }
              handleClearBtn={() => {
                dispatch(auditLogActions.setActiveEntitlementFilters([]));
                setSearchValue('');
              }}
              isClearBtn={activeReduxFilters.length > 0}
              searchDebounce={300}
              className={
                isTxFiltersOpen
                  ? 'SodAdvancedFilters--isOpen'
                  : 'SodAdvancedFilters--isClose'
              }
              endpoint={auditEndpoint}
            />

            {/* Transaction List */}
            <div
              className={CSS_CLASSES.TRANSACTION_LIST}
              ref={txListContainerRef}>
              {(isTxLoading || !isTxLoaded) && txItems.length === 0 && (
                <Box className='AuditLogViewerAuditorLeftBody-skeletonList'>
                  {SKELETON_KEYS.map((key) => (
                    <div
                      key={key}
                      className='AuditLogViewerAuditorLeftBody-skeletonItem'>
                      <div className='AuditLogViewerAuditorLeftBody-skelIcon' />
                      <div className='AuditLogViewerAuditorLeftBody-skelContent'>
                        <div className='AuditLogViewerAuditorLeftBody-skelTitle' />
                        <div className='AuditLogViewerAuditorLeftBody-skelDetails'>
                          <div className='AuditLogViewerAuditorLeftBody-skelBar' />
                          <div className='AuditLogViewerAuditorLeftBody-skelBar' />
                          <div className='AuditLogViewerAuditorLeftBody-skelBar' />
                        </div>
                      </div>
                      <div className='AuditLogViewerAuditorLeftBody-skelActions'>
                        <div className='AuditLogViewerAuditorLeftBody-skelButton' />
                        <div className='AuditLogViewerAuditorLeftBody-skelButton-kebob' />
                      </div>
                    </div>
                  ))}
                </Box>
              )}

              {(() => {
                if (isTxError) {
                  return (
                    <Box className='AuditLogViewerAuditorLeftBody-errorState'>
                      <Box className='Aag-TimeLineBlock-empty'>
                        <Box className='Aag-TimeLineBlock-emptyContent'>
                          <Illustration
                            kind='Error'
                            className='Aag-TimeLineBlock-emptyIcon'
                          />
                          <Typography
                            lineLimit={3}
                            kind='h4'
                            className='Aag-TimeLineBlock-emptyTitle'>
                            {l('Something went wrong while loading the page.')}
                          </Typography>
                          <Typography
                            lineLimit={3}
                            kind='body2'
                            className='Aag-TimeLineBlock-emptyDesc'>
                            {l(
                              'Try refreshing, or check back later. If the issue persists, contact your administrator.'
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Box className='AuditLogViewerAuditorLeftBody-errorActions'>
                        <Button
                          size='small'
                          kind='filled'
                          onClick={() => setTxReloadKey((k) => k + 1)}>
                          {l('Refresh')}
                        </Button>
                      </Box>
                    </Box>
                  );
                }

                // Always render current rows; show empty only when loaded and no items
                if (filteredAndSortedItems.length > 0) {
                  return (
                    <ListRowGroup className='AuditLogViewerAuditorLeftBody--isListRowGroup'>
                      {filteredAndSortedItems.map(renderTransactionItem)}
                    </ListRowGroup>
                  );
                }

                if (isTxLoaded && filteredAndSortedItems.length === 0) {
                  // Check if filters are active
                  const hasActiveFilters =
                    searchValue.length > 0 || activeReduxFilters.length > 0;

                  // Show filter message if filters are active
                  // If filters are active and filtered results are empty, it means filters filtered everything out
                  const isEmptyTitle = hasActiveFilters
                    ? l('No transactions found matching your filters')
                    : l('No activity recorded for this session');
                  const isEmptyDesc = hasActiveFilters
                    ? l(
                        'Try adjusting your filters or search terms to see more results.'
                      )
                    : l(
                        'This session was not used, so there are no recordings or log details to review.'
                      );

                  return (
                    <Box className='Aag-TimeLineBlock-empty'>
                      <Box className='Aag-TimeLineBlock-emptyContent'>
                        <Illustration
                          kind='Empty'
                          className='Aag-TimeLineBlock-emptyIcon'
                        />
                        <Typography
                          lineLimit={3}
                          kind='h4'
                          className='Aag-TimeLineBlock-emptyTitle'>
                          {isEmptyTitle}
                        </Typography>
                        <Typography
                          lineLimit={3}
                          kind='body2'
                          className='Aag-TimeLineBlock-emptyDesc'>
                          {isEmptyDesc}
                        </Typography>
                        {/* Show clear filters button when filters are active, Exit button when no filters */}
                        {hasActiveFilters ? (
                          <Button
                            kind='filled'
                            size='small'
                            onClick={() => {
                              dispatch(
                                auditLogActions.setActiveEntitlementFilters([])
                              );
                              setSearchValue('');
                            }}>
                            {l('Clear Filters')}
                          </Button>
                        ) : (
                          <Button
                            kind='filled'
                            size='small'
                            onClick={() => {
                              window.location.replace(
                                '/ECMv6/request/requestHome'
                              );
                            }}>
                            {l('Exit')}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                }

                return null;
              })()}
              {/* Bottom loader + sentinel (ViolationsTab-like) */}
              {!isTxError && afterKey !== null && (
                <>
                  {isTxLoading && (
                    <Box className='AuditLogViewerAuditorLeftBody-skeletonList'>
                      {SKELETON_KEYS.slice(0, 1).map((key) => (
                        <div
                          key={`${key}-bottom`}
                          className='AuditLogViewerAuditorLeftBody-skeletonItem'>
                          <div className='AuditLogViewerAuditorLeftBody-skelIcon' />
                          <div className='AuditLogViewerAuditorLeftBody-skelContent'>
                            <div className='AuditLogViewerAuditorLeftBody-skelTitle' />
                            <div className='AuditLogViewerAuditorLeftBody-skelDetails'>
                              <div className='AuditLogViewerAuditorLeftBody-skelBar' />
                              <div className='AuditLogViewerAuditorLeftBody-skelBar' />
                              <div className='AuditLogViewerAuditorLeftBody-skelBar' />
                            </div>
                          </div>
                          <div className='AuditLogViewerAuditorLeftBody-skelActions'>
                            <div className='AuditLogViewerAuditorLeftBody-skelButton' />
                            <div className='AuditLogViewerAuditorLeftBody-skelButton-kebob' />
                          </div>
                        </div>
                      ))}
                    </Box>
                  )}
                  {txListObserverElement}
                </>
              )}
            </div>
          </>
        )}
      </Box>

      {/* Insights Panel - Only visible when All Logs tab is active, panel is open, and not in readOnly mode */}
      {activeTab === 'all-logs' && isInsightsOpen && !isReadOnly && (
        <Box className='Sod-AuditLogViewer-insightsPanel'>
          <InsightsPanel onClose={handleInsightsClose} />
        </Box>
      )}

      {/* Recording Panel - Only visible when All Logs tab is active and panel is open */}
      {activeTab === 'all-logs' && isRecordingOpen && isVideoEnabled && (
        <Box className='Sod-AuditLogViewer-recordingPanel'>
          <Box className='Sod-AuditLogViewer-recordingPanel-header'>
            <Typography lineLimit={3} kind='h2'>
              <span>{l('Session Recording')}</span>
            </Typography>
            <ButtonIcon
              size='medium'
              kind='ghost'
              ariaLabel={l('recording')}
              icon={<Icon kind='Close' size='medium' />}
              onClick={handleRecordingToggle}
            />
          </Box>
          <Box
            className={
              isRecordingPlayerView
                ? 'Sod-AuditLogViewer-recordingPanel-content-playlist-view'
                : 'Sod-AuditLogViewer-recordingPanel-content'
            }>
            {(() => {
              if (recordedSessions.length === 0) {
                return (
                  <Box className='Sod-AuditLogViewer-recordingPanel-content-empty'>
                    <Typography
                      kind='body2'
                      color='--color-foreground-neutral-subtle'>
                      {l('No Session Recording Found')}
                    </Typography>
                  </Box>
                );
              }

              const cardSessions = recordedSessions.map((session) => ({
                id: session.id,
                src: session.previewSrc,
                title: session.title,
                dateTime: session.dateTime,
                duration: session.duration,
              }));

              const getSessionAt = (
                idx: number
              ): (typeof recordedSessions)[number] | undefined => {
                if (idx < 0 || idx >= recordedSessions.length) {
                  return undefined;
                }

                return recordedSessions[idx];
              };

              const selectedSession =
                getSessionAt(recordingSelectedIndex) ?? getSessionAt(0);

              if (isRecordingPlayerView) {
                // Defensive: in rare transient states (e.g., selection index becoming invalid
                // due to sessions list changing), avoid dereferencing undefined.
                if (!selectedSession) {
                  return (
                    <Box className='Sod-AuditLogViewer-recordingPanel-content-empty'>
                      <Typography
                        kind='body2'
                        color='--color-foreground-neutral-subtle'>
                        {l('No Session Recording Found')}
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <>
                    <AuditLogViewerVideoPlaylist
                      sessions={recordedSessions.map((session) => ({
                        id: session.id,
                        title: session.title,
                        meta: `${session.dateTime} • ${session.duration}`,
                      }))}
                      selectedIndex={recordingSelectedIndex}
                      onSelect={(index: number) => {
                        setRecordingSelectedIndex(index);
                        setIsRecordingPlayerView(true);
                        setMp4SeekSeconds(null);
                      }}
                      ariaPrevLabel={l('Previous')}
                      ariaNextLabel={l('Next')}
                    />
                    <Box className='Sod-Recording-playlist-video-player'>
                      <SessionRecording
                        key={`auditor-rec-session-${selectedSession.id}`}
                        launchId={selectedSession.id}
                        launchStartDate={selectedSession.launchStartDate}
                        seekSeconds={mp4SeekSeconds ?? undefined}
                      />
                    </Box>
                  </>
                );
              }

              // Render immediately - AuditLogViewerRecordingFrame is not lazy-loaded in auditor view
              return (
                <>
                  {Array.from({
                    length: Math.ceil(cardSessions.length / 2),
                  }).map((_, frameIdx) => (
                    <AuditLogViewerRecordingFrame
                      key={`rec-frame-${frameIdx + 1}`}
                      baseIndex={frameIdx * 2}
                      sessions={cardSessions}
                      onSelect={(index: number) => {
                        setRecordingSelectedIndex(index);
                        setIsRecordingPlayerView(true);
                        setMp4SeekSeconds(null);
                      }}
                    />
                  ))}
                </>
              );
            })()}
          </Box>
        </Box>
      )}

      {/* Detail Panel - Only visible when All Logs tab is active and panel is open */}
      {activeTab === 'all-logs' &&
        isDetailPanelOpen &&
        resolvedDetailPanelItem && (
          <AuditLogDetailPanel
            item={resolvedDetailPanelItem}
            onClose={handleDetailPanelClose}
            defaultActiveTab={detailPanelActiveTab}
            onUtcLogStartTimeClick={
              isVideoEnabled && hasRecordedSessions
                ? handleDetailPanelUtcTimestampClick
                : undefined
            }
            onViewRecording={
              isVideoEnabled && hasRecordedSessions
                ? handleDetailPanelUtcTimestampClick
                : undefined
            }
          />
        )}

      {/* Transaction Side Panel - Only visible when Transaction Overview tab is active and panel is open */}
      {activeTab === 'transaction-overview' &&
        panelStates.isTransactionSidePanelOpen &&
        selectedTransaction && (
          <AuditLogViewerAuditorTransactionSidePanel
            item={selectedTransaction}
            onClose={handleTransactionSidePanelClose}
            onViewLogs={handleViewLogs}
          />
        )}
    </Box>
  );
}

export default AuditLogViewerAuditorLeftBody;
